import './dcms-csrf.js';
import { Modals, DCMSPopup } from './modals.js';
import { Draggable } from './draggable.js';
import { Selectable } from './selectable.js';
import { Searchable } from './searchable.js';
import { FormValidationHandler } from './formValidationHandler.js';
import { Gallery } from './gallery.js';
import { FilterPills } from './filterPills.js';
import { DCMSLoader } from './dcms-loader.js';
import { DCMSSceneLoader } from './dcms-scene-loader.js';
import './dcms-modal-loader.js';
import { CharCounter } from './charCounter.js';
import './dcms-action-dock.js';
import './dcms-tab-bar.js';
import './dcms-daterange-calendar.js';
import './dcms-calendar.js';
import './dcms-native-picker.js';
import './dcms-dropdown-viewport-clamp.js';
import './dcms-form-fields.js';
import './dcms-file-input.js';
import './dcms-media-file-value.js';
import './dcms-avatar.js';
import './dcms-breadcrumbs.js';

const DCMS2JSPath = "/DCMS2/js/";

/**
 * FavoritePages class - manages the list of favorite pages in the rightbar
 *
 * Data source: user_settings.options[dcms2][rightbar][favorites] (server).
 * The #favorite-pages-list container supplies the initial state via data-initial
 * and the save URL via data-save-url (API: /api/v3.0/do/saveFavoritePages/).
 *
 * localStorage ('dcmsFavoritePages') acts as an offline cache — on startup
 * the server wins, and if the connection is unavailable we keep the local state.
 *
 * Single-entry model: {url, title, customTitle?}
 *  - title       : the page's original title (automatic, historical)
 *  - customTitle : optional title set by the user (shown if set)
 */
class FavoritePages {
    constructor() {
        this.storageKey = 'dcmsFavoritePages';
        this.maxItems = 20;
        this.saveDebounceMs = 300;
        this.container = document.getElementById('favorite-pages-list');
        this.saveUrl = this.container?.dataset.saveUrl || '';
        this.labels = this.readLabels();
        this.pages = [];
        this.revision = 0;
        this.saveTimer = null;
        this.renamingUrl = null;
        this.init();
    }

    readLabels() {
        const ds = this.container?.dataset || {};
        return {
            empty:         ds.labelEmpty        || 'Brak ulubionych stron',
            remove:        ds.labelRemove       || 'Usuń z ulubionych',
            rename:        ds.labelRename       || 'Zmień nazwę',
            renameSave:    ds.labelRenameSave   || 'Zapisz nazwę',
            renameCancel:  ds.labelRenameCancel || 'Anuluj',
            syncError:     ds.labelSyncError    || 'Nie udało się zsynchronizować ulubionych',
        };
    }

    init() {
        // Priority: data from the server (data-initial) > localStorage
        let initialFromServer = null;
        if (this.container?.dataset.initial) {
            try {
                const parsed = JSON.parse(this.container.dataset.initial);
                if (Array.isArray(parsed)) initialFromServer = parsed;
            } catch {
                APP.DCMSDebugLog('FavoritePages: invalid data-initial JSON', 2, 'FavoritePages');
            }
        }

        if (initialFromServer !== null) {
            this.pages = initialFromServer;
            this.writeLocalCache(this.pages);

            // Migration: if the server is empty but localStorage has something,
            // move the old favorites to the profile (one-off).
            if (this.pages.length === 0) {
                const legacy = this.readLocalCache();
                if (legacy.length > 0) {
                    this.pages = legacy;
                    this.scheduleSync();
                }
            }
        } else {
            // No data-initial (e.g. iframe render) — work locally
            this.pages = this.readLocalCache();
        }

        this.render();
        this.updateAddButton();
    }

    readLocalCache() {
        try {
            const raw = JSON.parse(localStorage.getItem(this.storageKey));
            return Array.isArray(raw) ? raw : [];
        } catch {
            return [];
        }
    }

    writeLocalCache(pages) {
        try {
            localStorage.setItem(this.storageKey, JSON.stringify(pages));
        } catch {
            // localStorage may be unavailable (private mode) — ignore
        }
    }

    /**
     * Save the current state: local cache + debounced sync with the server
     */
    persist() {
        this.revision++;
        this.writeLocalCache(this.pages);
        this.scheduleSync();
    }

    scheduleSync() {
        if (!this.saveUrl) return;
        if (this.saveTimer) clearTimeout(this.saveTimer);
        this.saveTimer = setTimeout(() => this.syncToServer(), this.saveDebounceMs);
    }

    async syncToServer() {
        if (!this.saveUrl) return;
        const revision = this.revision;
        try {
            const form = new FormData();
            form.append('pages', JSON.stringify(this.pages));
            const response = await fetch(this.saveUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
                body: form,
            });
            if (!response.ok) {
                throw new Error('HTTP ' + response.status);
            }
            const json = await response.json().catch(() => null);
            // The server may have sanitized the data — use the server's version, but only
            // when nothing changed locally while the request was in flight: the newer local
            // state is already queued for its own sync and must not be overwritten (and then
            // sent back) with this older answer.
            if (json && Array.isArray(json.pages) && revision === this.revision) {
                this.pages = json.pages;
                this.writeLocalCache(this.pages);
                // A rerender would reset the title being typed in the rename input
                if (this.renamingUrl === null) {
                    this.render();
                }
                this.updateAddButton();
            }
        } catch (error) {
            APP.DCMSDebugLog('FavoritePages sync failed: ' + error, 1, 'FavoritePages');
            if (typeof window.iziToast !== 'undefined') {
                window.iziToast.warning({ message: this.labels.syncError, position: 'bottomRight', timeout: 3000 });
            }
        }
    }

    isCurrentPageFavorite() {
        return this.pages.some(p => p.url === window.location.href);
    }

    addCurrentPage() {
        const url = window.location.href;
        const title = document.title || url;

        if (this.pages.some(p => p.url === url)) return false;

        this.pages.unshift({ url, title });
        this.pages = this.pages.slice(0, this.maxItems);

        this.persist();
        this.render();
        this.updateAddButton();
        return true;
    }

    removePage(url) {
        this.pages = this.pages.filter(p => p.url !== url);
        this.persist();
        this.render();
        this.updateAddButton();
    }

    startRename(url) {
        this.renamingUrl = url;
        this.render();
        // Focus the input after the rerender
        requestAnimationFrame(() => {
            const input = this.container?.querySelector('.favorite-rename-input');
            if (input) {
                input.focus();
                input.select();
            }
        });
    }

    cancelRename() {
        this.renamingUrl = null;
        this.render();
    }

    commitRename(url, newName) {
        const page = this.pages.find(p => p.url === url);
        if (!page) {
            this.renamingUrl = null;
            this.render();
            return;
        }
        const trimmed = (newName || '').trim();
        if (trimmed === '' || trimmed === page.title) {
            // Empty or identical to the original — remove customTitle
            delete page.customTitle;
        } else {
            page.customTitle = trimmed;
        }
        this.renamingUrl = null;
        this.persist();
        this.render();
    }

    updateAddButton() {
        const btn = document.getElementById('favorite-add-btn');
        if (!btn) return;

        const isFavorite = this.isCurrentPageFavorite();
        const icon = btn.querySelector('.msr');
        const text = btn.querySelector('.favorite-btn-text');

        if (isFavorite) {
            btn.disabled = true;
            btn.classList.add('opacity-50', 'cursor-not-allowed');
            btn.classList.remove('hover:bg-gray-100', 'dark:hover:bg-gray-700');
            if (icon) icon.textContent = 'check';
            if (text) text.textContent = 'Już w ulubionych';
        } else {
            btn.disabled = false;
            btn.classList.remove('opacity-50', 'cursor-not-allowed');
            btn.classList.add('hover:bg-gray-100', 'dark:hover:bg-gray-700');
            if (icon) icon.textContent = 'add';
            if (text) text.textContent = 'Dodaj do ulubionych';
        }
    }

    render() {
        if (!this.container) return;

        if (this.pages.length === 0) {
            this.container.innerHTML = `<div class="text-gray-500 dark:text-gray-400 text-sm px-4 py-2">${this.escapeHtml(this.labels.empty)}</div>`;
            return;
        }

        this.container.innerHTML = this.pages.map(page => this.renderItem(page)).join('');
        this.bindItemHandlers();
    }

    renderItem(page) {
        const displayTitle = page.customTitle || page.title || page.url;
        const tooltip = page.customTitle ? `${page.customTitle} — ${page.title}` : page.title;
        const isRenaming = this.renamingUrl === page.url;
        const urlAttr = this.escapeAttr(page.url);

        if (isRenaming) {
            return `
            <div class="flex items-center group px-4 py-1.5" data-fav-item data-fav-url="${urlAttr}">
                <span class="msr text-base mr-2 text-gray-400">star</span>
                <input type="text"
                       class="favorite-rename-input flex-1 min-w-0 px-2 py-1 text-sm rounded
                              bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600
                              text-gray-800 dark:text-gray-200
                              focus:outline-none focus:ring-1 focus:ring-blue-500"
                       value="${this.escapeAttr(displayTitle)}"
                       maxlength="255">
                <button type="button"
                        data-fav-action="rename-save"
                        class="p-1 ml-1 text-gray-400 hover:text-green-500"
                        title="${this.escapeAttr(this.labels.renameSave)}">
                    <span class="msr text-base">check</span>
                </button>
                <button type="button"
                        data-fav-action="rename-cancel"
                        class="p-1 text-gray-400 hover:text-red-500"
                        title="${this.escapeAttr(this.labels.renameCancel)}">
                    <span class="msr text-base">close</span>
                </button>
            </div>`;
        }

        return `
            <div class="flex items-center group" data-fav-item data-fav-url="${urlAttr}">
                <a href="${urlAttr}"
                   class="flex-1 flex items-center py-2 px-4 rounded-l text-sm transition-all duration-200
                          hover:bg-gray-100 dark:hover:bg-gray-700 truncate min-w-0"
                   title="${this.escapeAttr(tooltip)}">
                    <span class="msr text-base mr-2 text-gray-400">star</span>
                    <span class="truncate text-gray-800 dark:text-gray-200">${this.escapeHtml(displayTitle)}</span>
                </a>
                <button type="button"
                        data-fav-action="rename"
                        class="p-2 text-gray-400 hover:text-blue-500 dark:hover:text-sky-400
                               opacity-0 group-hover:opacity-100 transition-all duration-200"
                        title="${this.escapeAttr(this.labels.rename)}">
                    <span class="msr text-base">edit</span>
                </button>
                <button type="button"
                        data-fav-action="remove"
                        class="p-2 text-gray-400 hover:text-red-500 dark:hover:text-red-400
                               opacity-0 group-hover:opacity-100 transition-all duration-200"
                        title="${this.escapeAttr(this.labels.remove)}">
                    <span class="msr text-base">close</span>
                </button>
            </div>`;
    }

    bindItemHandlers() {
        this.container.querySelectorAll('[data-fav-item]').forEach(item => {
            const url = item.dataset.favUrl;

            item.querySelectorAll('[data-fav-action]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const action = btn.dataset.favAction;
                    if (action === 'remove') this.removePage(url);
                    else if (action === 'rename') this.startRename(url);
                    else if (action === 'rename-save') {
                        const input = item.querySelector('.favorite-rename-input');
                        this.commitRename(url, input?.value || '');
                    }
                    else if (action === 'rename-cancel') this.cancelRename();
                });
            });

            const input = item.querySelector('.favorite-rename-input');
            if (input) {
                input.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        this.commitRename(url, input.value);
                    } else if (e.key === 'Escape') {
                        e.preventDefault();
                        this.cancelRename();
                    }
                });
                input.addEventListener('blur', () => {
                    // Small delay so that a save/cancel click has a chance to fire first
                    setTimeout(() => {
                        if (this.renamingUrl === url) {
                            this.commitRename(url, input.value);
                        }
                    }, 150);
                });
            }
        });
    }

    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = String(text ?? '');
        return div.innerHTML;
    }

    escapeAttr(text) {
        return String(text ?? '')
            .replace(/&/g, '&amp;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    }
}

/**
 * RecentPages class - manages the list of recently visited pages
 * Stores the history in localStorage and displays it in the rightbar
 */
class RecentPages {
    constructor() {
        this.storageKey = 'dcmsRecentPages';
        this.maxItems = 10;
        this.container = document.getElementById('recent-pages-list');
        this.homeUrl = this.container?.dataset.homeUrl || '';
        this.init();
    }

    init() {
        // Record the current page
        this.addCurrentPage();

        // Listen for URL changes (popstate for back/forward navigation)
        window.addEventListener('popstate', () => this.addCurrentPage());

        // Render the list in the rightbar
        this.render();
    }

    /**
     * Checks whether the URL is the home page — in that case we don't record it in the history.
     * Matching: exact match with homeUrl, or the same origin + pathname '/'.
     */
    isHomePage(url) {
        if (!url) return false;
        try {
            const u = new URL(url);
            if (this.homeUrl) {
                // Normalize the trailing slash on both sides
                const normalized = url.replace(/\/+$/, '');
                const homeNormalized = this.homeUrl.replace(/\/+$/, '');
                if (normalized === homeNormalized) return true;
            }
            // Fallback: pathname '/' (bare origin) is always treated as home
            if (u.pathname === '/' || u.pathname === '') return true;
        } catch {
            // Unparseable URL — leave it
        }
        return false;
    }

    addCurrentPage() {
        const url = window.location.href;

        // Skip the home page — don't clutter the history
        if (this.isHomePage(url)) {
            return;
        }

        const title = document.title || url;
        const timestamp = Date.now();

        let pages = this.getPages();

        // Remove the duplicate if it exists
        pages = pages.filter(p => p.url !== url);

        // Add to the front
        pages.unshift({ url, title, timestamp });

        // Limit to maxItems
        pages = pages.slice(0, this.maxItems);

        this.savePages(pages);
        this.render();
    }

    getPages() {
        try {
            return JSON.parse(localStorage.getItem(this.storageKey)) || [];
        } catch {
            return [];
        }
    }

    savePages(pages) {
        localStorage.setItem(this.storageKey, JSON.stringify(pages));
    }

    getRelativeTime(timestamp) {
        const seconds = Math.floor((Date.now() - timestamp) / 1000);
        if (seconds < 60) return 'przed chwilą';
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes} min temu`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours} godz. temu`;
        const days = Math.floor(hours / 24);
        return `${days} dni temu`;
    }

    render() {
        const container = document.getElementById('recent-pages-list');
        if (!container) return;

        const pages = this.getPages();

        if (pages.length === 0) {
            container.innerHTML = '<div class="text-gray-500 dark:text-gray-400 text-sm px-4">Brak ostatnich stron</div>';
            return;
        }

        container.innerHTML = pages.map(page => `
            <a href="${this.escapeHtml(page.url)}"
               class="flex items-center justify-between py-2 px-4 rounded text-sm transition-all duration-200
                      hover:bg-gray-100 dark:hover:bg-gray-700 group">
                <span class="truncate text-gray-800 dark:text-gray-200">${this.escapeHtml(page.title)}</span>
                <span class="text-xs text-gray-400 dark:text-gray-500 ml-2 whitespace-nowrap">${this.getRelativeTime(page.timestamp)}</span>
            </a>
        `).join('');
    }

    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}

/**
 * DevTools class - developer debugging panel in the rightbar
 * Receives data from the iframe via postMessage and displays it in the rightbar
 * Only active when the environment != Production and the user is an admin
 *
 * Entries are untrusted input: every field is escaped before it lands in the DOM
 * and no entry value is placed in inline event handlers.
 */
class DevTools {
    constructor() {
        this.entries = [];
        this.categories = new Set();
        this.init();
    }

    init() {
        // Listen for postMessage from the iframe — only from our own origin and
        // only from a frame of this page. A foreign page that frames DCMS2 or
        // opens it in a window must not be able to inject entries.
        window.addEventListener('message', (event) => {
            if (event.origin !== window.location.origin || !this.isOwnFrame(event.source)) {
                return;
            }
            if (event.data?.type === 'dcms-devtools') {
                this.addEntries(event.data.entries);
            }
        });

        // Also listen for CustomEvent (for the no-iframe mode)
        window.addEventListener('dcms-devtools', (event) => {
            if (event.detail?.entries) {
                this.addEntries(event.detail.entries);
            }
        });

        this.render();
    }

    /**
     * Checks whether the message source is one of this page's frames
     * (window.frames — the page's iframes, including the ones in modals)
     */
    isOwnFrame(source) {
        if (!source) return false;
        for (let i = 0; i < window.frames.length; i++) {
            if (window.frames[i] === source) return true;
        }
        return false;
    }

    addEntries(entries) {
        if (!Array.isArray(entries)) return;

        entries.forEach(entry => {
            if (!entry || typeof entry !== 'object') return;

            // Check whether the entry already exists (by id)
            if (!this.entries.some(e => e.id === entry.id)) {
                this.entries.push(entry);
                if (entry.category) {
                    this.categories.add(entry.category);
                }
            }
        });

        this.render();
    }

    clear() {
        this.entries = [];
        this.categories.clear();
        this.render();
    }

    formatValue(value, depth = 0, indent = '') {
        const nextIndent = indent + '  ';

        if (depth > 6) return '<span class="text-gray-400">...</span>';

        if (value === null) return '<span class="text-purple-500">null</span>';
        if (value === undefined) return '<span class="text-purple-500">undefined</span>';
        if (typeof value === 'boolean') return `<span class="text-blue-500 dark:text-sky-400">${value}</span>`;
        if (typeof value === 'number') return `<span class="text-green-600 dark:text-green-400">${value}</span>`;
        if (typeof value === 'string') {
            // Cut before escaping, so the cut never splits an HTML entity
            if (value.length > 200) {
                return `<span class="text-orange-600 dark:text-orange-400">"${this.escapeHtml(value.substring(0, 197))}..."</span>`;
            }
            return `<span class="text-orange-600 dark:text-orange-400">"${this.escapeHtml(value)}"</span>`;
        }

        if (Array.isArray(value)) {
            if (value.length === 0) return '<span class="text-gray-500">[]</span>';

            // Short arrays - inline
            if (value.length <= 3 && depth > 0) {
                const items = value.map(v => this.formatValue(v, depth + 1, nextIndent)).join(', ');
                return `[${items}]`;
            }

            // Longer arrays - multiline
            const items = value.slice(0, 20).map((v, i) => {
                return `\n${nextIndent}<span class="text-gray-400">[${i}]</span> ${this.formatValue(v, depth + 1, nextIndent)}`;
            }).join('');
            const more = value.length > 20 ? `\n${nextIndent}<span class="text-gray-400">...+${value.length - 20} more</span>` : '';
            return `<span class="text-gray-500">Array(${value.length})</span> [${items}${more}\n${indent}]`;
        }

        if (typeof value === 'object') {
            // Handle PHP objects (with __class)
            if (value.__class) {
                const className = this.escapeHtml(String(value.__class).split('\\').pop());
                const fullClass = this.escapeHtml(value.__class);
                const data = this.formatValue(value.__data, depth + 1, nextIndent);
                return `<span class="text-cyan-600 dark:text-cyan-400" title="${fullClass}">${className}</span> ${data}`;
            }

            const keys = Object.keys(value);
            if (keys.length === 0) return '<span class="text-gray-500">{}</span>';

            // For deep levels - shortened view
            if (depth > 3) {
                return `<span class="text-gray-500">{${keys.length} keys}</span>`;
            }

            // Multiline format
            const items = keys.slice(0, 30).map(k => {
                const keyDisplay = this.escapeHtml(k);
                return `\n${nextIndent}<span class="text-sky-600 dark:text-sky-400">${keyDisplay}</span>: ${this.formatValue(value[k], depth + 1, nextIndent)}`;
            }).join('');
            const more = keys.length > 30 ? `\n${nextIndent}<span class="text-gray-400">...+${keys.length - 30} more</span>` : '';
            return `{${items}${more}\n${indent}}`;
        }

        return this.escapeHtml(String(value));
    }

    getRelativeTime(timestamp) {
        const seconds = Math.floor((Date.now() / 1000) - timestamp);
        if (seconds < 60) return 'teraz';
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m`;
        const hours = Math.floor(minutes / 60);
        return `${hours}h`;
    }

    formatTime(timestamp) {
        const date = new Date(timestamp * 1000);
        const h = String(date.getHours()).padStart(2, '0');
        const m = String(date.getMinutes()).padStart(2, '0');
        const s = String(date.getSeconds()).padStart(2, '0');
        return `${h}:${m}:${s}`;
    }

    extractNamespace(filePath) {
        if (typeof filePath !== 'string' || !filePath) return '';
        // Looking for a pattern like AMPD/DCMS2/... or AMPD/Framework/... etc.
        const match = filePath.match(/AMPD[\/\\](.+?)\.php$/i);
        if (match) {
            // Replace / and \ with \
            return 'AMPD\\' + match[1].replace(/[\/\\]/g, '\\');
        }
        // Fallback - return just the file name
        return filePath.split(/[\/\\]/).pop().replace('.php', '');
    }

    getLevelIcon(level) {
        switch (level) {
            case 'error': return '<span class="msr text-red-500 text-sm">error</span>';
            case 'warn': return '<span class="msr text-yellow-500 text-sm">warning</span>';
            case 'info': return '<span class="msr text-blue-500 dark:text-sky-400 text-sm">info</span>';
            default: return '<span class="msr text-gray-400 text-sm">code</span>';
        }
    }

    toggleEntry(id) {
        const entry = document.querySelector(`.devtools-entry[data-id="${CSS.escape(String(id))}"]`);
        if (!entry) return;

        const content = entry.querySelector('.devtools-content');
        const expandIcon = entry.querySelector('.devtools-expand-icon');

        if (content.classList.contains('hidden')) {
            content.classList.remove('hidden');
            expandIcon.textContent = 'expand_less';
        } else {
            content.classList.add('hidden');
            expandIcon.textContent = 'expand_more';
        }
    }

    copyEntry(id) {
        const entry = this.entries.find(e => e.id === id);
        if (!entry) return;

        const textToCopy = JSON.stringify(entry.data, null, 2);
        navigator.clipboard.writeText(textToCopy).then(() => {
            // Brief feedback - change the icon for a moment
            const btn = document.querySelector(`.devtools-entry[data-id="${CSS.escape(String(id))}"] .devtools-copy-btn`);
            if (btn) {
                const icon = btn.querySelector('.msr');
                icon.textContent = 'check';
                setTimeout(() => { icon.textContent = 'content_copy'; }, 1000);
            }
        });
    }

    removeEntry(id) {
        this.entries = this.entries.filter(e => e.id !== id);
        this.render();
    }

    render() {
        const container = document.getElementById('devtools-entries');
        const noEntriesEl = document.getElementById('devtools-no-entries');

        if (!container) return;

        if (this.entries.length === 0) {
            if (noEntriesEl) noEntriesEl.style.display = '';
            container.querySelectorAll('.devtools-entry').forEach(el => el.remove());
            return;
        }

        if (noEntriesEl) noEntriesEl.style.display = 'none';

        // Remove old entries
        container.querySelectorAll('.devtools-entry').forEach(el => el.remove());

        // Render entries (newest first)
        const sortedEntries = [...this.entries].sort((a, b) => b.timestamp - a.timestamp);

        sortedEntries.forEach(entry => {
            const div = document.createElement('div');
            div.className = 'devtools-entry p-2 rounded bg-gray-50 dark:bg-gray-800 text-xs font-mono mb-2';
            div.setAttribute('data-id', entry.id);

            const icon = entry.entryType === 'log' ? this.getLevelIcon(entry.level) : '<span class="msr text-gray-400 text-sm">data_object</span>';
            const label = this.escapeHtml(entry.label || 'debug');
            const time = this.formatTime(entry.timestamp);
            const namespace = this.extractNamespace(entry.file);
            const line = parseInt(entry.line, 10) || '';
            const location = namespace ? `${this.escapeHtml(namespace)}:${line}` : '';

            let content = '';
            if (entry.entryType === 'log') {
                content = `<span class="text-gray-700 dark:text-gray-200">${this.escapeHtml(String(entry.data))}</span>`;
            } else {
                content = this.formatValue(entry.data);
            }

            div.innerHTML = `
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-1 cursor-pointer devtools-header">
                        <span class="msr text-gray-400 text-sm devtools-expand-icon">expand_more</span>
                        ${icon}
                        <span class="font-semibold text-gray-800 dark:text-gray-100">${label}</span>
                        <span class="text-gray-400 text-[10px] ml-1">${time}</span>
                    </div>
                    <div class="flex items-center gap-0.5">
                        <button class="devtools-copy-btn p-0.5 hover:text-blue-500 dark:hover:text-sky-400 transition-colors" title="Kopiuj">
                            <span class="msr text-sm">content_copy</span>
                        </button>
                        <button class="devtools-remove-btn p-0.5 hover:text-red-500 transition-colors" title="Usuń">
                            <span class="msr text-sm">close</span>
                        </button>
                    </div>
                </div>
                <div class="devtools-content hidden overflow-x-auto whitespace-pre break-words text-[11px] leading-relaxed mt-1 pl-5">${content}</div>
                ${location ? `<div class="text-[10px] text-gray-400 pl-5">${location}</div>` : ''}
            `;

            // Handlers are bound here, so entry.id never ends up in inline code
            div.querySelector('.devtools-header').addEventListener('click', () => this.toggleEntry(entry.id));
            div.querySelector('.devtools-copy-btn').addEventListener('click', () => this.copyEntry(entry.id));
            div.querySelector('.devtools-remove-btn').addEventListener('click', () => this.removeEntry(entry.id));

            container.appendChild(div);
        });
    }

    /**
     * Escapes text for HTML content and attribute values (quotes included —
     * __class goes into a title attribute)
     */
    escapeHtml(text) {
        return String(text ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }
}

class APP {
	modalsInstance;
	draggableInstance;
	selectableInstance;
	searchableInstance;
	galleryInstance;
	filterPillsInstance;
	
    constructor() {
		// Merge instead of overwrite — modules imported above (e.g.
		// dcms-scene-loader.js attaching DCMS2.loader) may have already
		// created the namespace.
		window.DCMS2 = Object.assign({
		    version: '2.0.1',
		    initialized: true
		}, window.DCMS2 || {});
		
        this.loadedScripts = new Map();
        
        this.modalsInstance = new Modals();
        this.draggableInstance = new Draggable();
        this.selectableInstance = new Selectable();
        this.searchableInstance = new Searchable();
        this.galleryInstance = new Gallery();
		this.filterPillsInstance = new FilterPills();

        // Expose DCMSPopup globally (for inline scripts in the iframe)
        window.DCMSPopup = DCMSPopup;

        this.init();
    }
      
     /**
     * Global logging function with debug level control and a prefix
     * @param {string} message - Message to log
     * @param {number} level - Message severity level
     * @param {string} prefix - Prefix (usually the class name)
     */
    static DCMSDebugLog(message, level = 3, prefix = 'DCMS') {
        const DEBUG_LEVEL = typeof window.DCMS_DEBUG_LEVEL !== 'undefined' ? window.DCMS_DEBUG_LEVEL : 0;
        
        if (DEBUG_LEVEL >= level) {
            const formattedMessage = `[${prefix}]: ${message}`;
            if (level === 1) {
                console.error(formattedMessage);
            } else if (level === 2) {
                console.warn(formattedMessage);
            } else {
                console.log(formattedMessage);
            }
        }
    }  
	
	initMenu() {
		const menuButton = document.getElementById('menu-button');
		const that = this;
		if (menuButton != null){
			menuButton.addEventListener('click', function() {
			if ($("#menu").hasClass('hidden')) {
				$.ajax({ url: $("#menu").data('url') }).done(function(data) {
					$('#sidebar-left .spinner').remove();

					// Check whether the response is not empty
					if (!data || data.trim() === '') {
						APP.DCMSDebugLog('initMenu: server returned an empty response — retrying in 1s', 1, 'APP.initMenu');
						that._menuRetryCount = 0;
						setTimeout(() => that.reloadMenu(), 1000);
						return;
					}

					$('#menu ul').html(data);

					// Mark the active menu item based on the current URL
					that.markActiveMenuItem();

					$('#menu').removeClass('hidden');

                    // CHANGE: use the new, Promise-based loadScript function
					that.loadScript( "menu.filter.js", 'local.menu.filter')
                        .then(() => {
                            // This code runs only after the script has loaded successfully
                            new menuFilter().init({filtrIn: '#menuFilter'});

                            // Scroll the menu container to the active position
                            const activeItem = document.querySelector('#menu .active > a.m-p') || document.querySelector('#menu .active:first-child');
                            if (activeItem) {
                                activeItem.scrollIntoView({ block: 'center' });
                            }
					    })
                        .catch(error => {
                            // Handle errors if the script fails to load
                            APP.DCMSDebugLog(`Nie udało się załadować skryptu menu.filter.js: ${error}`, 1, 'APP.initMenu');
                        });
				});
			}
		});
		$(document).on('click', '[data-toggle="collapse"]', (e) => this.collapse(e));
		}
	}

	/**
	 * Reloads the menu in the sidebar.
	 *
	 * Forces a fetch of the current menu data from the server (the /menu endpoint),
	 * replaces the sidebar's DOM content, and re-initializes the menu filter.
	 * Called e.g. after clearing the CMS cache (postMessage from the iframe).
	 *
	 * Flow:
	 *  1. Gets the menu URL from the data-url attribute of the #menu element
	 *  2. Performs an AJAX GET with a cache-buster parameter (avoids browser cache)
	 *  3. Replaces the HTML inside #sidenav-menu with the new content
	 *  4. Marks the active menu item (markActiveMenuItem)
	 *  5. Re-initializes the menu filter (menuFilter)
	 *
	 * @returns {void}
	 */
	reloadMenu() {
		const menuEl = document.getElementById('menu');
		if (!menuEl) {
			APP.DCMSDebugLog('reloadMenu: element #menu nie znaleziony', 2, 'APP.reloadMenu');
			return;
		}

		const menuUrl = menuEl.getAttribute('data-url');
		if (!menuUrl) {
			APP.DCMSDebugLog('reloadMenu: brak data-url na #menu', 2, 'APP.reloadMenu');
			return;
		}

		const that = this;

		// Retry counter — max 3 retries with exponential backoff
		if (typeof this._menuRetryCount === 'undefined') {
			this._menuRetryCount = 0;
		}

		// Add a cache-buster to skip the browser cache
		const cacheBuster = menuUrl + (menuUrl.includes('?') ? '&' : '?') + '_t=' + Date.now();

		// Vanilla JS fetch — avoids jQuery (will eventually be removed)
		fetch(cacheBuster, {
			method: 'GET',
			headers: { 'X-Requested-With': 'XMLHttpRequest' }
		})
		.then(response => {
			if (!response.ok) {
				throw new Error('HTTP ' + response.status);
			}
			return response.text();
		})
		.then(html => {
			// Check whether the response is not empty
			if (!html || html.trim() === '') {
				if (that._menuRetryCount < 3) {
					that._menuRetryCount++;
					const delay = Math.min(1000 * Math.pow(2, that._menuRetryCount - 1), 8000);
					APP.DCMSDebugLog('reloadMenu: serwer zwrócił pustą odpowiedź — ponowna próba (' + that._menuRetryCount + '/3) za ' + delay + 'ms', 1, 'APP.reloadMenu');
					setTimeout(() => that.reloadMenu(), delay);
				} else {
					APP.DCMSDebugLog('reloadMenu: serwer zwrócił pustą odpowiedź po 3 próbach — menu niedostępne', 1, 'APP.reloadMenu');
					that._menuRetryCount = 0;
				}
				return;
			}

			// Reset the counter after a successful load
			that._menuRetryCount = 0;

			// Replace the menu content
			const menuList = document.querySelector('#menu ul');
			if (menuList) {
				menuList.innerHTML = html;
			}

			// Make sure the menu is visible
			menuEl.classList.remove('hidden');

			// Mark the active position
			that.markActiveMenuItem();

			// Re-initialize the menu filter
			that.loadScript('menu.filter.js', 'local.menu.filter')
				.then(() => {
					new menuFilter().init({ filtrIn: '#menuFilter' });

					// Scroll to the active position
					const activeItem = document.querySelector('#menu .active > a.m-p')
						|| document.querySelector('#menu .active:first-child');
					if (activeItem) {
						activeItem.scrollIntoView({ block: 'center' });
					}
				})
				.catch(error => {
					APP.DCMSDebugLog('reloadMenu: nie udało się załadować menu.filter.js: ' + error, 1, 'APP.reloadMenu');
				});

			APP.DCMSDebugLog('Menu przeładowane pomyślnie', 3, 'APP.reloadMenu');
		})
		.catch(error => {
			APP.DCMSDebugLog('reloadMenu: błąd pobierania menu: ' + error, 1, 'APP.reloadMenu');
		});
	}

	/**
	 * Marks the active menu item and expands its parents
	 * Compares the current URL against the menu links (href attributes)
	 */
	markActiveMenuItem() {
		const currentPath = window.location.pathname.replace(/\/+$/, '');
		if (!currentPath) return;

		const menuContainer = document.getElementById('sidenav-menu');
		if (!menuContainer) return;

		// Look for a menu link matching the current URL
		const allLinks = menuContainer.querySelectorAll('a.m-p');
		let activeLink = null;

		for (const link of allLinks) {
			const href = link.getAttribute('href');
			if (!href) continue;
			const linkPath = href.replace(/\/+$/, '');
			if (linkPath === currentPath) {
				activeLink = link;
				break;
			}
		}

		if (!activeLink) {
			APP.DCMSDebugLog('No matching menu item found for: ' + currentPath, 3, 'APP.markActiveMenuItem');
			return;
		}

		// 1. Mark the <li> of the active link
		const activeLi = activeLink.closest('li');
		if (activeLi) {
			activeLi.classList.add('active');
		}

		// 2. Expand and mark all parents (folders)
		let parentUl = activeLi ? activeLi.parentElement : null;
		while (parentUl) {
			// If this is a <ul> with class c-l (collapsible list) — expand it
			if (parentUl.classList.contains('c-l')) {
				parentUl.classList.add('in');
			}

			// Mark the parent <li> as active
			const parentLi = parentUl.parentElement;
			if (parentLi && parentLi.tagName === 'LI') {
				parentLi.classList.add('active');
				parentUl = parentLi.parentElement;
			} else {
				break;
			}
		}

		APP.DCMSDebugLog('Active menu item marked: ' + activeLink.querySelector('.m-t')?.textContent, 3, 'APP.markActiveMenuItem');
	}

	/**
	     * NEW VERSION OF THE FUNCTION
	     * Loads a script, ensuring that the same file is never loaded more than once.
	     * The function is idempotent – calling it repeatedly with the same URL will not
	     * cause repeated loading.
	     * @param {string} scriptUrl - Path to the script.
	     * @param {string} scriptName - Script name, used to handle local paths (`local.`).
	     * @returns {Promise<void>} - Promise that resolves once the script has loaded successfully.
	     */
		loadScript(scriptUrl, scriptName){
	        let finalUrl = scriptUrl;
			if (scriptName && scriptName.startsWith('local.')) {
				finalUrl = DCMS2JSPath + scriptUrl;	
	        }
		  
	        // 1. Check whether the script is already in our registry (loading or loaded).
	        if (this.loadedScripts.has(finalUrl)) {
	            APP.DCMSDebugLog(`Script already requested, returning existing Promise for: ${finalUrl}`, 3, 'APP.loadScript');
	            return this.loadedScripts.get(finalUrl);
	        }

	        APP.DCMSDebugLog(`Requesting new script: ${finalUrl}`, 3, 'APP.loadScript');

	        // 2. Create a new Promise and IMMEDIATELY store it in the registry.
	        //    This is essential so that subsequent, concurrent calls to this function for the
	        //    same URL get the same Promise instead of creating new requests.
	        const promise = new Promise((resolve, reject) => {
	            $.getScript(finalUrl)
	                .done(() => {
	                    APP.DCMSDebugLog(`Successfully loaded script: ${finalUrl}`, 3, 'APP.loadScript');
	                    resolve();
	                })
	                .fail((jqxhr, settings, exception) => {
	                    APP.DCMSDebugLog(`Error loading script: ${finalUrl}. Reason: ${exception}`, 1, 'APP.loadScript');
	                    // On error, remove the Promise from the map to allow a retry in the future.
	                    this.loadedScripts.delete(finalUrl);
	                    reject(new Error(`Could not load script: ${finalUrl}`));
	                });
	        });

	        this.loadedScripts.set(finalUrl, promise);

		    return promise;
		}
	
	initDocumentReady() {
		const that = this;
		$(document).ready(function() {
			iFrameResize({
				log: false,
				heightCalculationMethod: 'lowestElement',
				checkOrigin: false,
				tolerance: 32,
				scrolling: false,
				autoResize: true,
				onScroll: function() {
					return false;
				},
				onResized: function(data) {
					APP.DCMSDebugLog('iFrameResize: ' + data.iframe.id + ' h=' + data.height, 3, 'APP');
				}
			}, '.iframeContent');

			that.handleThemes();
		
		$('iframe.iframeContent').each(function() {
	        var src = $(this).data('src');
	        $(this).attr('src', src);
    	});
		
		});
		
		
	}

	initThemes() {
		const darkModeButton = document.getElementById('dark-mode-switch');
		if (darkModeButton != null)
			darkModeButton.addEventListener('click', ev => this.handleThemes(ev));
	}

	/**
	 * Gradient background toggle - saves the preference to localStorage
	 */
	initGradientBg() {
		const btn = document.getElementById('gradient-bg-toggle');
		const bg = document.querySelector('.dcms-gradient-bg');
		if (!btn || !bg) return;

		const key = 'dcmsGradientBg';
		const saved = localStorage.getItem(key);

		// Enabled by default; if the user turned it off -> hide
		if (saved === 'off') {
			bg.style.display = 'none';
			btn.classList.add('opacity-40');
		}

		btn.addEventListener('click', () => {
			const isVisible = bg.style.display !== 'none';
			bg.style.display = isVisible ? 'none' : '';
			btn.classList.toggle('opacity-40', isVisible);
			localStorage.setItem(key, isVisible ? 'off' : 'on');
		});
	}

	initNotifications(){
		window.showToast = function(title, message, type = '', iconText = 'notification', iconClass = "msr", targetWindow = window, timeout = 7500, maxWidth = 300, position = "topRight", id = '') {
				if (!id)
				  id = Date.now().toString(36) + Math.random().toString(36).substr(2);
				targetWindow.iziToast.show({
					timeout: timeout,
					id: id,
					progressBar: true,
					icon: iconClass,
					iconText: iconText,
					titleLineHeight: 25,
					color: type,
					title: title,
					message: message,
					position: position,
					maxWidth: maxWidth
				});
		};

	}


	initI18n(){
		window.__i18n = function(word, section = 'commonjs', defaultTranslation = '') {    
	    let table = 'i18n_' + section;
	    if (window[table] && window[table][word])
	        return window[table][word];
	    if (defaultTranslation.length)
	    	return defaultTranslation;
	    return word;
		}	
	};
	
	
	updateTabErrorStateForFieldInit() {
	    /**
	     * Helper function to update a tab's error state for a given field.
	     * @param {HTMLElement} fieldElement - The form field element.
	     * @param {boolean} hasError - Whether the field has an error.
	     * @param {Array<HTMLElement>} elementsToStyleArray - Array of elements that are styled (e.g. input, select2-container).
	     */
	    /**
	     * Marks the collapsible panels (<details class="panel-accordion">, e.g. EditLayout
	     * collapsible()) that hold the field: panel-accordion-has-error on the panel — a red title
	     * with an error icon (main.css), visible also while the panel is collapsed. The marker
	     * comes off once no other field of the panel shows an error; the elements of this field
	     * (elementsToStyleArray) do not count, as in the tab marker below.
	     * @param {HTMLElement} fieldElement - The form field element.
	     * @param {boolean} hasError - Whether the field has an error.
	     * @param {Array<HTMLElement>} elementsToStyleArray - Elements styled for the field.
	     */
	    window.updatePanelErrorStateForField = function(fieldElement, hasError, elementsToStyleArray) {
	        const ownElements = [fieldElement].concat(elementsToStyleArray || []);
	        for (let panel = fieldElement.closest('details.panel-accordion'); panel; panel = panel.parentElement?.closest('details.panel-accordion')) {
	            if (hasError) {
	                panel.classList.add('panel-accordion-has-error');
	                continue;
	            }
	            const errorsInPanel = Array.from(panel.querySelectorAll('.validationError, [data-parsley-id].parsley-error'))
	                .filter((el) => !ownElements.includes(el)).length;
	            if (errorsInPanel === 0) {
	                panel.classList.remove('panel-accordion-has-error');
	            }
	        }
	    };

	    /**
	     * Re-checks every collapsible panel of the document against the errors it shows (server-side
	     * errors rendered with the form, the end of a Parsley form validation).
	     */
	    window.syncPanelErrorStates = function(root) {
	        (root || document).querySelectorAll('details.panel-accordion').forEach((panel) => {
	            const hasError = panel.querySelector('.validationError, [data-parsley-id].parsley-error') !== null;
	            panel.classList.toggle('panel-accordion-has-error', hasError);
	        });
	    };

	    window.updateTabErrorStateForField = function(fieldElement, hasError, elementsToStyleArray) { // Added elementsToStyleArray as an argument
	        window.updatePanelErrorStateForField(fieldElement, hasError, elementsToStyleArray);
	        const tabPane = fieldElement.closest('.tab-pane');
	        DCMSDebugLog(`updateTabErrorStateForField: Field='${fieldElement.id}', HasError='${hasError}', TabPane found: ${!!tabPane}`, 3, 'APP.updateTabErrorStateForField');

	        if (tabPane) {
	            const navId = tabPane.id + '-nav';
	            const tabLabel = document.getElementById(navId);

	            if (tabLabel) {
	                DCMSDebugLog(`TabLabel found: ID='${navId}', CurrentClasses='${tabLabel.className}'`, 3, 'APP.updateTabErrorStateForField');
	                if (hasError) {
	                    if (!tabLabel.classList.contains('dcms-tab-has-error')) {
	                        tabLabel.classList.add('dcms-tab-has-error');
	                        DCMSDebugLog(`Added 'dcms-tab-has-error' to TabLabel ID='${navId}'`, 3, 'APP.updateTabErrorStateForField');
	                    }
	                } else {
	                    // Errors still shown on this tab by other fields. The elements of this field
	                    // (the field, its `_1` part, the Select2 container — elementsToStyleArray)
	                    // do not count: their classes are already cleared, and a parsley-error class
	                    // Parsley has not removed yet does not make this field an error again.
	                    // Formerly one remaining error counted as this field's own, so a field turning
	                    // valid cleared the marker although another field on the tab was still an error.
	                    const ownElements = [fieldElement].concat(elementsToStyleArray || []);
	                    const errorsOnTab = Array.from(tabPane.querySelectorAll('.validationError, [data-parsley-id].parsley-error'))
	                        .filter((el) => !ownElements.includes(el)).length;

	                    if (errorsOnTab === 0) {
	                        if (tabLabel.classList.contains('dcms-tab-has-error')) {
	                            tabLabel.classList.remove('dcms-tab-has-error');
	                            DCMSDebugLog(`Removed 'dcms-tab-has-error' from TabLabel ID='${navId}' because no other errors found.`, 3, 'APP.updateTabErrorStateForField');
	                        }
	                    } else {
	                        DCMSDebugLog(`Kept 'dcms-tab-has-error' on TabLabel ID='${navId}' due to other errors on tab (${errorsOnTab}).`, 3, 'APP.updateTabErrorStateForField');
	                    }
	                }
	            } else {
	                DCMSDebugLog(`TabLabel NOT found for navId='${navId}'`, 2, 'APP.updateTabErrorStateForField');
	            }
	        }
	    }
	    DCMSDebugLog('initialized', 3, 'APP.updateTabErrorStateForField');
	}
	
	/**
	 * Function that displays styles after validation
	 */
	formValidation() {
	    this.updateTabErrorStateForFieldInit();
	    window.setValidationState = function(id, state, message) {
	        const element = document.getElementById(id);
	        const element2 = document.getElementById(id + '_1'); // For fields made up of two parts
	        const helpElement = document.getElementById(`${id}-helpText`);
	        const helpContainer = document.getElementById(`${id}-help`);

	        DCMSDebugLog(`setValidationState: Element ID='${id}', State='${state}', Message='${message}'`, 3, 'APP.setValidationState');

	        if (!element) {
	            DCMSDebugLog(`Element with id '${id}' not found. Cannot set validation state.`, 1, "APP.setValidationState");
	            return;
	        }

	        // Elements to which validation classes will be added/removed
	        const elementsToStyle = [element]; // Variable defined here
	        if (element2) {
	            elementsToStyle.push(element2);
	        }

	        // Special handling for Select2
	        let targetElementForParsley = element; // Element on which Parsley operates
	        if (element.getAttribute('data-select2-lib') === 'true') {
	            const findNextSiblingWithClass = (el, className) => {
	                while (el = el.nextElementSibling) {
	                    if (el.classList.contains(className)) return el;
	                }
	                return null;
	            };
	            const select2Container = findNextSiblingWithClass(element, 'select2-container');
	            if (select2Container) {
	                elementsToStyle.push(select2Container); // Add the Select2 container for styling
	                targetElementForParsley = select2Container; // Parsley may operate on the container
	            }
	        }

	        // Whether the field showed an error before this call (see state 0 below)
	        const hadError = elementsToStyle.some(el => el.classList.contains('validationError'));

	        // Remove existing validation classes and message
	        elementsToStyle.forEach(el => el.classList.remove('validationSuccess', 'validationError'));
	        if (helpElement) {
	            helpElement.textContent = '';
	            helpElement.classList.remove('validationSuccessMessage', 'validationErrorMessage');
	        }
	        if (helpContainer) {
	            helpContainer.style.display = 'none';
	        }

	        // Check whether the form has validation success coloring disabled
	        const noValidationColors = element.closest('form[data-no-validation-colors]') !== null;

	        // Setting the new validation state
	        switch (state) {
	            case 1: // Success (valid)
	                if (!noValidationColors) {
	                    elementsToStyle.forEach(el => el.classList.add('validationSuccess'));
	                    if (helpElement && message) {
	                        helpElement.classList.add('validationSuccessMessage');
	                        helpElement.textContent = message;
	                        if (helpContainer) helpContainer.style.display = 'block';
	                    }
	                }
	                // If the field is valid, we potentially remove the error from the tab
	                updateTabErrorStateForField(element, false, elementsToStyle); // We pass elementsToStyle
	                break;

	            case 2: // Error
	                elementsToStyle.forEach(el => el.classList.add('validationError'));
	                // A collapsed section (<details>, e.g. a collapsible form panel) would hide the error
	                for (let section = element.closest('details'); section; section = section.parentElement?.closest('details')) {
	                    section.open = true;
	                }
	                if (helpElement && message) {
	                    helpElement.classList.add('validationErrorMessage');
	                    helpElement.textContent = message;
	                    if (helpContainer) helpContainer.style.display = 'block';
	                }
	                // If the field has an error, we mark the tab
	                updateTabErrorStateForField(element, true, elementsToStyle); // We pass elementsToStyle
	                break;
		
		        default: // Reset state
		            if (helpContainer) {
		                helpContainer.style.display = 'none';
		            }
		            // A field that showed an error and no longer does: the tab keeps its marker only
		            // for other fields. The reset every field template calls on load (state 0 for a
		            // field without an error) leaves the markers alone — pages also mark tabs for
		            // errors that have no field of their own (e.g. the CRM order forms).
		            if (hadError) {
		                updateTabErrorStateForField(element, false, elementsToStyle);
		            }
		            break;
		    }
		}
		APP.DCMSDebugLog('setValidationState function initialized.', 3, 'APP.formValidation');

        // Initialize FormValidationHandler, if Parsley and the language are available
        if (window.ParsleyValidator && typeof DCMS_CURRENT_LANGUAGE !== 'undefined') {
            this.formValidationHandlerInstance = new FormValidationHandler(
                DCMS_CURRENT_LANGUAGE,
                window.setValidationState, // Pass a reference to the global function
                APP.DCMSDebugLog          // Pass a reference to the logging function
            );
        } else {
             if (!window.ParsleyValidator) {
                APP.DCMSDebugLog('ParsleyValidator not found when trying to init FormValidationHandler.', 2, 'APP.formValidation');
            }
            if (typeof DCMS_CURRENT_LANGUAGE === 'undefined') {
                APP.DCMSDebugLog('DCMS_CURRENT_LANGUAGE not defined when trying to init FormValidationHandler.', 2, 'APP.formValidation');
            }
        }
	}
	
	/**
	 * validates a field using Parsley.js given its id
	 */
	validateField(id) {
    // Get the field element based on the given id
    var field = document.getElementById(id);
    if (field) {
        // Initialize Parsley on the field, if not already done
        var parsleyField = $(field).parsley();
        // Run field validation (displays error messages)
        parsleyField.validate();
        // Check whether the field is valid
        var isValid = parsleyField.isValid();
        // Return the validation result: true if valid, false if invalid
        return isValid;
    } else {
        // If the field with the given id doesn't exist, return false
        return false;
    }
}
	formSelect2() {
		/* Moves focus to the next element after the select closes	*/
		window.select2TabNextFocus = function(el) {
			let fe = $(el.form).find(':input:enabled:visible');
			let tid = el.id;
			
			$(fe).each(function(i,e) {
			
				let u = i;
				
				if(e.nodeName.toLowerCase() == 'option') {
					e = e.parentNode;
					u++;
				}
				if(e.id == tid) {
					u++;
					$(fe[u]).focus();
					return false;
				}
			});
			}
	}
	
	formCheckboxFilter() {
		window.createCheckboxFilter = function(el) {
			el._chb = [];
			el.showAll = false;

			if (el.querySelectorAll('input[type="checkbox"]').length > 10 || el.classList.contains('filterCheckboxHideList')) {
				el.querySelectorAll('label').forEach(function(label) {
					if (!document.getElementById(label.getAttribute('for')).checked) {
						document.getElementById(label.getAttribute('for') + '-group').style.display = 'none';
					}
				});
			}

			// Plain "contains" match, case-insensitive, whitespace collapsed to one space —
			// the typed text is never used as a regular expression (same as the React port).
			var normalizeFilterText = function(text) {
				return String(text).replace(/\s+/g, ' ').trim().toLocaleLowerCase();
			};

			el.querySelectorAll('label').forEach(function(label) {
				let labelText = normalizeFilterText(label.textContent);
				if (labelText !== '') {
					el._chb.push({ txt: labelText, group: document.getElementById(label.getAttribute('for') + '-group') });
				}
			});

			var nid = (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);

			var filterContainer = document.createElement('div');
			filterContainer.className = 'flex flex-wrap w-full';
			filterContainer.innerHTML = `
      <div class="flex items-center cursor-pointer rounded-full h-9 w-9 justify-center transition-all duration-300 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800" id="createChbFilter_${nid}_filter">
        <span class="material-symbols-rounded text-xl">filter_alt</span>
      </div>
      <div class="flex items-center cursor-pointer rounded-full h-9 w-9 justify-center transition-all duration-300 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800" id="createChbFilter_${nid}_all">
        <span class="material-symbols-rounded text-xl">list</span>
      </div>
      <div class="flex flex-grow relative">
        <input type="text" class="w-full h-8 pl-8 pr-4 rounded-md" id="createChbFilter_${nid}" data-parsley-excluded="true">
        <span class="material-symbols-rounded text-xl absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400">search</span>
      </div>
    `;

			el.prepend(filterContainer);

			var inp = document.getElementById(`createChbFilter_${nid}`);
			inp.el = el;

			inp.onkeyup = function() {
				el.querySelectorAll('label').forEach(function(label) {
					if (!document.getElementById(label.getAttribute('for')).checked) {
						document.getElementById(label.getAttribute('for') + '-group').style.display = 'none';
					}
				});

				var filterIcon = document.getElementById(`createChbFilter_${nid}_filter`).querySelector('span');
				filterIcon.classList.remove('text-red-500', 'dark:text-red-200');

				var needle = normalizeFilterText(this.value);
				if (needle !== '') {
					filterIcon.classList.add('text-red-500', 'dark:text-red-200');
					el._chb.forEach(function(chb) {
						if (chb.txt.includes(needle)) {
							chb.group.style.display = '';
						}
					});
				} else {
					el.querySelectorAll('label').forEach(function(label) {
						if (document.getElementById(label.getAttribute('for')).checked) {
							document.getElementById(label.getAttribute('for') + '-group').style.display = '';
						}
					});
				}
			};

			document.getElementById(`createChbFilter_${nid}_filter`).onclick = function() {
				inp.value = '';
				inp.onkeyup();
			};

			document.getElementById(`createChbFilter_${nid}_all`).onclick = function() {
				el.showAll = !el.showAll;
				if (el.showAll)
					el.querySelectorAll('label').forEach(function(label) {
						document.getElementById(label.getAttribute('for') + '-group').style.display = '';
					});
				else
					el.querySelectorAll('label').forEach(function(label) {
						if (!document.getElementById(label.getAttribute('for')).checked)
							document.getElementById(label.getAttribute('for') + '-group').style.display = 'none';
					});

				// Show/hide the "Select/Deselect all" buttons
				var bulkBtns = el.querySelector('.dcms-chb-select-deselect');
				if (bulkBtns) {
					bulkBtns.style.display = el.showAll ? '' : 'none';
				}
			};
		}

  document.addEventListener('DOMContentLoaded', function() {
    document.querySelectorAll('.filterCheckbox').forEach(function(element) {
      createCheckboxFilter(element);
    });
  });
}


	initForms() {

		this.formValidation();
		this.formCheckboxFilter();
		this.formSelect2();
		this.formMoney();
		this.formHelpText();
		this.charCounterInstance = new CharCounter();
		this.charCounterInstance.init();
	}

	/**
	 * helpText handling — displays a tooltip next to the label.
	 * Supports two modes: hover (default) and click (for mobile / when the tooltip contains links).
	 * The mode is determined by the data-help-mode attribute on .dcms-help-icon ('hover' or 'click').
	 * Uses event delegation, so it also handles dynamically added elements.
	 */
	formHelpText() {
		// --- HOVER mode ---
		document.addEventListener('mouseenter', function(e) {
			if (!e.target || !e.target.closest) return;
			const icon = e.target.closest('.dcms-help-icon');
			if (!icon) return;
			const mode = icon.getAttribute('data-help-mode') || 'hover';
			if (mode !== 'hover') return;
			const targetId = icon.getAttribute('data-help-target');
			if (!targetId) return;
			const tooltip = document.getElementById(targetId);
			if (tooltip) {
				tooltip.classList.add('dcms-help-visible');
			}
		}, true);

		document.addEventListener('mouseleave', function(e) {
			if (!e.target || !e.target.closest) return;
			const icon = e.target.closest('.dcms-help-icon');
			if (!icon) return;
			const mode = icon.getAttribute('data-help-mode') || 'hover';
			if (mode !== 'hover') return;
			const targetId = icon.getAttribute('data-help-target');
			if (!targetId) return;
			const tooltip = document.getElementById(targetId);
			if (tooltip) {
				tooltip.classList.remove('dcms-help-visible');
			}
		}, true);

		// --- CLICK mode ---
		document.addEventListener('click', function(e) {
			const icon = e.target.closest('.dcms-help-icon');
			if (icon) {
				const mode = icon.getAttribute('data-help-mode') || 'hover';
				if (mode !== 'click') return;
				e.preventDefault();
				e.stopPropagation();
				const targetId = icon.getAttribute('data-help-target');
				if (!targetId) return;
				const tooltip = document.getElementById(targetId);
				if (!tooltip) return;

				// close other open click-tooltips
				document.querySelectorAll('.dcms-help-tooltip.dcms-help-visible').forEach(function(el) {
					if (el !== tooltip) el.classList.remove('dcms-help-visible');
				});

				tooltip.classList.toggle('dcms-help-visible');
				return;
			}

			// click outside the tooltip — close open click-tooltips
			const openTooltip = e.target.closest('.dcms-help-tooltip');
			if (!openTooltip) {
				document.querySelectorAll('.dcms-help-tooltip.dcms-help-visible').forEach(function(el) {
					// check whether this is a click-mode tooltip
					const id = el.id;
					const relatedIcon = document.querySelector('.dcms-help-icon[data-help-target="' + id + '"]');
					if (relatedIcon && (relatedIcon.getAttribute('data-help-mode') || 'hover') === 'click') {
						el.classList.remove('dcms-help-visible');
					}
				});
			}
		});

		APP.DCMSDebugLog('HelpText hover+click handler initialized', 3, 'APP');
	}


	/**
	 * Formatting inputs with type money (data-foramt-money = "true")
	 */
	formMoney() {
	    /**
	     * Formats the given value into a monetary format (float with 2 decimal places)
	     * @param {string} value - Value to format
	     * @return {string} - Formatted value
	     */
	    function formatMoneyValue(value) {
	        // Remove all characters except digits, dots and commas
	        let cleanValue = value.replace(/[^\d.,]/g, '');
	        
	        // Replace commas with dots
	        cleanValue = cleanValue.replace(',', '.');
	        
	        // Check whether the value is a valid number
	        if (cleanValue === '' || isNaN(parseFloat(cleanValue))) {
	            return '';
	        }
	        
	        // Format the value to 2 decimal places
	        return parseFloat(cleanValue).toFixed(2);
	    }

	    /**
	     * Initializes formatting for all matching input fields
	     */
	    function initMoneyInputs() {
	        // Find all input fields with the data-format-money="true" attribute
	        const moneyInputs = document.querySelectorAll('input[data-format-money="true"]');
	        
	        moneyInputs.forEach(input => {
	            // Format the initial value
	            if (!input.readOnly && !input.disabled && input.value) {
	                input.value = formatMoneyValue(input.value);
	            }
	            
	            // Right-align the text
	            input.style.textAlign = 'right';
	            
	            // Handle the blur event (when the user finishes entering data)
	            input.addEventListener('blur', function(event) {
	                // Format only if the field is not readonly or disabled
	                if (!this.readOnly && !this.disabled && this.value) {
	                    this.value = formatMoneyValue(this.value);
	                }
	            });
	            
	            // Handle the paste event (when the user pastes data)
	            input.addEventListener('paste', function(event) {
	                // Format only if the field is not readonly or disabled
	                if (!this.readOnly && !this.disabled) {
	                    // Use setTimeout to give the browser time to paste the text
	                    setTimeout(() => {
	                        this.value = formatMoneyValue(this.value);
	                    }, 0);
	                }
	            });
	        });
	    }

	    // Run the initialization
	    if (document.readyState === 'loading') {
	        document.addEventListener('DOMContentLoaded', initMoneyInputs);
	    } else {
	        initMoneyInputs();
	    }

	    // Handle dynamically added elements
	    // Use a MutationObserver to detect DOM changes
	    const observer = new MutationObserver(mutations => {
	        mutations.forEach(mutation => {
	            if (mutation.addedNodes && mutation.addedNodes.length > 0) {
	                // Check whether the added nodes contain elements we care about
	                let hasNewMoneyInputs = false;
	                
	                mutation.addedNodes.forEach(node => {
	                    // Check whether this is an HTML element
	                    if (node.nodeType === 1) {
	                        // Check whether this is an input with data-format-money or contains such an input
	                        if ((node.tagName === 'INPUT' && node.getAttribute('data-format-money') === 'true') ||
	                            (node.querySelectorAll && node.querySelectorAll('input[data-format-money="true"]').length > 0)) {
	                            hasNewMoneyInputs = true;
	                        }
	                    }
	                });
	                
	                // If new money fields were found, initialize them
	                if (hasNewMoneyInputs) {
	                    initMoneyInputs();
	                }
	            }
	        });
	    });

	    // Start observing changes across the whole document
	    observer.observe(document.body, { 
	        childList: true, 
	        subtree: true 
	    });
	    
	    APP.DCMSDebugLog("Money input formatter initialized", 3, 'APP');
	}

	/**
	 * Initializes listening for postMessage messages from iframes.
	 *
	 * Supported message types (event.data.type):
	 *  - 'dcms-reload-menu' — reloads the menu in the sidebar (e.g. after clearing the cache)
	 *  - 'dcms-apply-settings' — immediately applies interface settings
	 *    (theme, layout) after saving in the Appearance Preferences form
	 *
	 * Messages are sent from iframes (e.g. ClearMenuCache, UserProfile) to the parent window (Master).
	 * Verifies the sender's origin — accepts only messages from the same domain.
	 *
	 * @returns {void}
	 */
	initIFrameMessages() {
		const that = this;

		window.addEventListener('message', function(event) {
			// Origin verification — accept only messages from our own domain
			if (event.origin !== window.location.origin) {
				return;
			}

			if (!event.data || typeof event.data !== 'object') {
				return;
			}

			switch (event.data.type) {
				case 'dcms-reload-menu':
					APP.DCMSDebugLog('Otrzymano postMessage: dcms-reload-menu', 3, 'APP.initIFrameMessages');
					that.reloadMenu();
					break;

				case 'dcms-apply-settings':
					APP.DCMSDebugLog('Otrzymano postMessage: dcms-apply-settings', 3, 'APP.initIFrameMessages');
					that.applyInterfaceSettings(event.data.settings || {});
					break;
			}
		});
	}
	
    applyInitialLayout() {
        const layoutCookie = this.getLayoutCookie();
        if (!layoutCookie) return;

        const layoutPreferences = JSON.parse(layoutCookie);

        const applyLayout = (doc) => {
            Object.keys(layoutPreferences).forEach(type => {
                Object.keys(layoutPreferences[type]).forEach(key => {
                    const layoutPreference = layoutPreferences[type][key];
                    const [layout, maxSize] = layoutPreference.split(',');

                    let containers = [];
                    if (type === 'id') {
                        const container = doc.getElementById(key);
                        if (container) {
                            containers.push(container);
                        }
                    } else if (type === 'class') {
                        containers = doc.querySelectorAll(`.${key}`);
                    }

                    containers.forEach(container => {
                        if (layout === 'fixed') {
                            container.classList.remove('w-full', 'dcms-fluid-width');
                            container.classList.add(maxSize, 'mx-auto', 'dcms-fixed-width');
                        } else {
                            container.classList.remove(maxSize, 'mx-auto', 'dcms-fixed-width');
                            container.classList.add('w-full', 'dcms-fluid-width');
                        }
                    });
                });
            });
        };

        applyLayout(document);

        document.querySelectorAll('iframe').forEach(iframe => {
            try {
                const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                applyLayout(iframeDoc);
            } catch (e) {
                 DCMSDebugLog('Cannot access iframe content:' + e, 1, "APP");
            }
        });
    }

	initListeners() {
        const toggleLayout = (button, doc) => {
            const containerId = button.getAttribute('data-id');
            const containerClass = button.getAttribute('data-class');
            const maxSize = button.getAttribute('data-max-size') || 'max-w-7xl';

            let containers = [];

            if (containerId) {
                const container = doc.getElementById(containerId);
                if (container) {
                    containers.push(container);
                }
            } else if (containerClass) {
                containers = doc.querySelectorAll(`.${containerClass}`);
            }

            containers.forEach(container => {
                if (container.classList.contains('w-full')) {
                    container.classList.remove('w-full', 'dcms-fluid-width');
                    container.classList.add(maxSize, 'mx-auto', 'dcms-fixed-width');
                    this.setLayoutCookie(containerId || containerClass, 'fixed', maxSize, containerId ? 'id' : 'class');
                } else {
                    container.classList.remove(maxSize, 'mx-auto', 'dcms-fixed-width');
                    container.classList.add('w-full', 'dcms-fluid-width');
                    this.setLayoutCookie(containerId || containerClass, 'fluid', '', containerId ? 'id' : 'class');
                }
            });
        };

        document.querySelectorAll('.toggleFixedFluidWidth').forEach(button => {
            button.addEventListener('click', () => {
                toggleLayout(button, document);
                // Apply layout changes to iframes
                document.querySelectorAll('iframe').forEach(iframe => {
                    try {
                        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                        toggleLayout(button, iframeDoc);
                    } catch (e) {
                        console.error('Cannot access iframe content:', e);
                    }
                });
            });
        });
    }

    applyLayoutFromCookie() {
	    const layoutCookie = this.getLayoutCookie();
	    if (!layoutCookie){
			document.body.classList.add('layout-ready');
			return;	  
		}
		

      const layoutPreferences = JSON.parse(layoutCookie);
	  const applyLayout = (doc) => {
	  return new Promise((resolve) => {
	    const promises = [];
	
	    Object.keys(layoutPreferences).forEach(type => {
	      Object.keys(layoutPreferences[type]).forEach(key => {
	        const layoutPreference = layoutPreferences[type][key];
	        const [layout, maxSize] = layoutPreference.split(',');
	
	        let containers = [];
	        if (type === 'id') {
	          const container = doc.getElementById(key);
	          if (container) {
	            containers.push(container);
	          }
	        } else if (type === 'class') {
	          containers = doc.querySelectorAll(`.${key}`);
	        }
	
	        containers.forEach(container => {
	          const promise = new Promise((resolveContainer) => {
	            requestAnimationFrame(() => {
	              if (layout === 'fixed') {
	                container.classList.remove('w-full', 'dcms-fluid-width');
	                container.classList.add(maxSize, 'mx-auto', 'dcms-fixed-width');
	              } else {
	                container.classList.remove(maxSize, 'mx-auto', 'dcms-fixed-width');
	                container.classList.add('w-full', 'dcms-fluid-width');
	              }
	              resolveContainer();
	            });
	          });
	          promises.push(promise);
	        });
	      });
	    });
	
		    Promise.all(promises).then(() => {
		      requestAnimationFrame(() => {
		        doc.body.classList.add('layout-ready');
		        resolve();
		      });
		    });
		  });
		};
	
		// Using the function
		applyLayout(document).then(() => {
		  console.log('Układ został w pełni zastosowany');
		});

        document.querySelectorAll('iframe').forEach(iframe => {
            try {
                const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                applyLayout(iframeDoc);
            } catch (e) {
                console.error('Cannot access iframe content:', e);
            }
        });
    }

    setLayoutCookie(key, layout, maxSize = '', type) {
        const value = `${layout}${maxSize ? ',' + maxSize : ''}`;

        let layoutPreferences = {};

        const layoutCookie = this.getLayoutCookie();
        if (layoutCookie) {
            layoutPreferences = JSON.parse(layoutCookie);
        }

        if (!layoutPreferences[type]) {
            layoutPreferences[type] = {};
        }

        layoutPreferences[type][key] = value;

        document.cookie = "dcmsLayout=" + encodeURIComponent(JSON.stringify(layoutPreferences)) + "; path=/; max-age=" + (3600 * 24 * 30);
    }

    getLayoutCookie() {
        const matches = document.cookie.match(new RegExp(
            "(?:^|; )dcmsLayout=([^;]*)"
        ));
        return matches ? decodeURIComponent(matches[1]) : undefined;
    }
    
	init() {
		this.initI18n();
	    this.applyLayoutFromCookie();
		this.initListeners();
		this.initDocumentReady();
		this.initMenu();
		this.initIFrameMessages();
		this.initThemes();
		this.initThemeSkinSync();
		this.initGradientBg();
		this.initNotifications();
		this.initForms();
		this.initContentIframeUrlSync();
		this.initIframeContentOverflow();
		this.initRecentPages();
		this.draggableInstance.init();
		this.selectableInstance.init();
		// Gallery initializes itself automatically in the constructor
	}

	/**
	 * Initialization of FavoritePages and RecentPages - favorite and recent pages in the rightbar
	 */
	initRecentPages() {
		// Initialize DevTools (only when the section exists - i.e. non-prod + admin)
		if (document.getElementById('devtools-entries')) {
			this.devToolsInstance = new DevTools();
			APP.DCMSDebugLog('DevTools initialized', 3, 'APP');
		}

		// Initialize favorites
		if (document.getElementById('favorite-pages-list')) {
			this.favoritePagesInstance = new FavoritePages();
			APP.DCMSDebugLog('FavoritePages initialized', 3, 'APP');
		}

		// Initialize recent pages
		if (document.getElementById('recent-pages-list')) {
			this.recentPagesInstance = new RecentPages();
			APP.DCMSDebugLog('RecentPages initialized', 3, 'APP');
		}
	}

	/**
	 * Syncs the top window's URL with the content iframe's state (deep links).
	 *
	 * Master writes the current URL into the content iframe once (DCMS::render → setIframeMode),
	 * but further navigation (returning from edit, PRG redirect after save) happens
	 * INSIDE the iframe — the top window's address bar was left with datasetMode=edit&datasetRowId=…
	 * and F5 / a copied address would reopen the edit form instead of the list.
	 *
	 * After every iframe load we mirror the dataset mode parameters (datasetMode,
	 * datasetRowId) into the top window's URL via history.replaceState — but only for
	 * per-record states (datasetRowId present: edit/view/clone/delete). Entering
	 * edit mode stamps a deep link into the address bar; leaving to the list / search / insert
	 * clears BOTH parameters — the address bar goes back to the menu position's generic address.
	 * No history entries (replaceState) — the Back button still works as before.
	 */
	initContentIframeUrlSync() {
		const iframe = document.querySelector('iframe.dcms-content-iframe');
		if (!iframe) return;
		iframe.addEventListener('load', () => {
			try {
				const ifLoc = iframe.contentWindow.location;
				if (ifLoc.origin !== window.location.origin) return;
				const ifParams = new URLSearchParams(ifLoc.search);
				const topUrl = new URL(window.location.href);
				const rowScoped = ifParams.get('datasetRowId') !== null;
				let changed = false;
				['datasetMode', 'datasetRowId'].forEach((p) => {
					const v = rowScoped ? ifParams.get(p) : null;
					if (v === null) {
						if (topUrl.searchParams.has(p)) {
							topUrl.searchParams.delete(p);
							changed = true;
						}
					} else if (topUrl.searchParams.get(p) !== v) {
						topUrl.searchParams.set(p, v);
						changed = true;
					}
				});
				if (changed) {
					history.replaceState(history.state, '', topUrl.toString());
					APP.DCMSDebugLog('Content iframe URL sync: ' + topUrl.search, 3, 'APP');
				}
			} catch (e) { /* cross-origin or no access — we don't synchronize anything */ }
		});
	}

	/**
	 * Fix for pages with the dcms-iframe-content class without an active iframe-resizer
	 * CSS sets overflow:hidden assuming iframe-resizer controls the height
	 * When iframe-resizer is not active, we enable scrolling
	 */
	initIframeContentOverflow() {
		if (document.body.classList.contains('dcms-iframe-content') && !window.parentIFrame) {
			document.body.style.overflow = 'auto';
			APP.DCMSDebugLog('Enabled scroll for dcms-iframe-content without iframe-resizer', 3, 'APP');
		}
	}

	isDarkMode(){
		return(document.documentElement.classList.contains('dark'));
	}
	
	handleThemes(e) {
		const darkModeEnabled = this.isDarkMode();
		DCMSDebugLog('dark mode: '+ darkModeEnabled, 3, "APP");
		const iframes = document.querySelectorAll('.iframeContent');

		// Iterate over each iframe
		iframes.forEach((iframe) => {
			if (iframe.contentDocument) {
				// Check whether the iframe contains a document
				const iframeHtml = iframe.contentDocument.documentElement;

				if (darkModeEnabled) {
					// Enable dark mode in the iframe
					iframeHtml.classList.add('dark');
				} else {
					// Disable dark mode in the iframe
					iframeHtml.classList.remove('dark');
				}
			}
		});
	}

	/**
	 * Applies the theme skin (data-theme attribute) to the main document
	 * and to all content iframes. 'default' removes the attribute.
	 *
	 * New iframes don't need propagation — the boot script in main.tpl reads
	 * localStorage['dcms_theme_skin'] before the first paint.
	 *
	 * @param {string} skin — 'default' | 'classic'
	 */
	applyThemeSkin(skin) {
		const apply = (html) => {
			if (!html) return;
			if (!skin || skin === 'default') {
				html.removeAttribute('data-theme');
			} else {
				html.setAttribute('data-theme', skin);
			}
		};
		apply(document.documentElement);
		document.querySelectorAll('.iframeContent').forEach((iframe) => {
			if (iframe.contentDocument) {
				apply(iframe.contentDocument.documentElement);
			}
		});
	}

	/**
	 * One-off sync of the theme skin from the user's profile
	 * (window.__DCMS_INTERFACE_SETTINGS__ from the session) into localStorage —
	 * for browsers without a local copy (fresh profile / cleared
	 * storage). An existing localStorage entry keeps priority, the same
	 * way as for the color mode.
	 */
	initThemeSkinSync() {
		try {
			if (localStorage.getItem('dcms_theme_skin') !== null) return;
			const settings = window.__DCMS_INTERFACE_SETTINGS__;
			const skin = settings && settings.themeSkin;
			if (skin && skin !== 'default') {
				localStorage.setItem('dcms_theme_skin', skin);
				this.applyThemeSkin(skin);
			}
		} catch(e) {}
	}
	
	/**
	 * Immediately applies interface settings from the "Appearance Preferences" form.
	 *
	 * Called via the 'dcms-apply-settings' postMessage from the form's iframe.
	 * Applies: theme (FrostUI + dark class), layout (cookie + CSS classes).
	 *
	 * @param {object} settings — {theme: 'light'|'dark'|'auto', pageLayout: 'fixed'|'fluid', ...}
	 */
	applyInterfaceSettings(settings) {
		if (!settings || typeof settings !== 'object') return;

		// ── Theme ──
		if (settings.theme) {
			var frostTheme = settings.theme === 'auto' ? 'system' : settings.theme;
			try {
				localStorage.setItem('frost.app_state', JSON.stringify({ theme: frostTheme }));
			} catch(e) {}

			// Apply dark/light on the main document
			var isDark = false;
			if (frostTheme === 'system') {
				isDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
			} else {
				isDark = (frostTheme === 'dark');
			}

			if (isDark) {
				document.documentElement.classList.add('dark');
			} else {
				document.documentElement.classList.remove('dark');
			}

			// Synchronize iframes
			this.handleThemes();

			APP.DCMSDebugLog('Theme applied: ' + frostTheme + ' (dark=' + isDark + ')', 3, 'APP.applyInterfaceSettings');
		}

		// ── Theme skin ──
		if (settings.themeSkin) {
			try {
				if (settings.themeSkin === 'default') {
					localStorage.removeItem('dcms_theme_skin');
				} else {
					localStorage.setItem('dcms_theme_skin', settings.themeSkin);
				}
			} catch(e) {}
			this.applyThemeSkin(settings.themeSkin);
			APP.DCMSDebugLog('Theme skin applied: ' + settings.themeSkin, 3, 'APP.applyInterfaceSettings');
		}

		// ── Layout ──
		if (settings.pageLayout) {
			this.applyLayoutFromCookie();
			APP.DCMSDebugLog('Layout applied from cookie: ' + settings.pageLayout, 3, 'APP.applyInterfaceSettings');
		}

		// ── Loading background (dcms-modal-loader.js) — windows of this page; frames get it with their next load ──
		if ((settings.loadingEffect || settings.loadingEffectIntensity) && window.DCMS2 && window.DCMS2.modalLoader) {
			window.DCMS2.modalLoader.configure({ user: { effect: settings.loadingEffect, intensity: settings.loadingEffectIntensity } });
		}
	}

	collapse(e) {
		e.stopPropagation();
	
		let target = e.target;
		let nodeName = target.nodeName.toLowerCase();

		if(nodeName !== 'a') {
			target = target.parentNode;
		}

		let targetHref = target.href;
		let href = targetHref.substring(targetHref.indexOf('#'),targetHref.length );

		$(target).parents('.h-sb:first').toggleClass('active');	
		$(href).toggleClass('in');

		return false;		
	}		
	

	static run(logLevel = 3){
		window.DCMS_DEBUG_LEVEL = logLevel; // For full logging
		window.DCMSDebugLog = APP.DCMSDebugLog;
		window.APP = new APP();		
		APP.DCMSDebugLog('Application initialized', 3, 'APP');
	}
	
}


APP.run(3);